import { fetchCsvExport } from '@/lib/utils/exports'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { PRICING_RATE_TYPE_VALUES, PRODUCT_CATEGORIES, type ProductCategory } from '@repo/constants'
import { monthStartString } from '@repo/zod-schemas'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

export const pricingRatesSearchSchema = z.object({
  view: z.enum(PRICING_RATE_TYPE_VALUES).optional(),
  type: z.array(z.enum(PRICING_RATE_TYPE_VALUES)).optional(),
  period: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  packContentSize: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  marking: z.array(z.string()).optional(),
  keyword: z.string().optional(),
  showValidStatus: z.boolean().optional(),
  crtPeriod: monthStartString.optional(),
  loadedVersionId: z.number().optional(),
  pricingAssumptionIds: z.array(z.number()).optional(),
  taxAssumptionIds: z.array(z.number()).optional(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
  sort: z
    .object({
      id: z.string(),
      desc: z.boolean(),
    })
    .optional(),
})

export type PricingRatesSearch = z.infer<typeof pricingRatesSearchSchema>

async function getPricingRates(search: PricingRatesSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['pricing-excise']['pricing-rates'].$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )

  return await res.json()
}

type PricingRatesExportFilters = {
  productCategory?: ProductCategory[]
  materialGroupCode?: string[]
  brand?: string[]
  variant?: string[]
  packContentSize?: string[]
  productType?: string[]
  marking?: string[]
  keyword?: string
  showValidStatus?: boolean
  pricingAssumptionIds?: number[]
  taxAssumptionIds?: number[]
  crtPeriod?: string
}

async function getPricingRatesCsv(filters: PricingRatesExportFilters, signal?: AbortSignal) {
  const stringifiedQueries = stringifyValues(filters)

  const filteredQueries = Object.fromEntries(
    Object.entries(stringifiedQueries).filter(([, value]) => value !== undefined),
  )
  const queryParams = new URLSearchParams(filteredQueries).toString()

  const csvExport = await fetchCsvExport(
    `/crt/pricing-excise/pricing-rates/export?${queryParams}`,
    signal,
  )
  return csvExport
}

export function pricingRatesExportQueryOptions(filters: PricingRatesExportFilters) {
  return {
    queryKey: ['pricingRatesExport', filters],
    queryFn: ({ signal }: { signal: AbortSignal }) => getPricingRatesCsv(filters, signal),
  }
}

export function usePricingRates(queries: PricingRatesSearch) {
  return useQuery({
    queryKey: ['pricingRates', queries],
    queryFn: ({ signal }) => getPricingRates(queries, signal),
  })
}
